import { Link } from "react-router-dom";
import { CheckCircle2, PhoneCall, ArrowRight, ShieldCheck } from "lucide-react";

export function ConsultBanner() {
  return (
    <section className="bg-[#0A192F] text-white relative">
      <div className="absolute top-0 left-0 right-0 h-1 bg-[#B91C1C]" />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 grid md:grid-cols-3 gap-8 items-center">
        <div className="md:col-span-2 flex items-start gap-5">
          <PhoneCall className="w-8 h-8 text-[#D97706] shrink-0 mt-1" strokeWidth={1.6} />
          <div>
            <div className="text-caption text-[#D97706] mb-2">COMPLIMENTARY</div>
            <h2 className="font-serif text-2xl lg:text-3xl font-bold leading-tight mb-2">Free 15-minute confidential consultation.</h2>
            <p className="text-slate-300 leading-relaxed max-w-xl">Every engagement begins with a complimentary, confidential 15-minute consultation to review the case, assess record volume, and confirm fit — before any fee is discussed.</p>
          </div>
        </div>
        <div className="flex md:justify-end">
          <Link to="/case-intake" data-testid="cta-free-consult" className="btn-crimson inline-flex items-center gap-2 px-6 py-3 text-[13px] uppercase tracking-widest">
            Book Free Consultation <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

export function ServiceCard({ s, i }) {
  return (
    <div className="card-federal p-8 flex flex-col" data-testid={`service-detail-${i}`}>
      <div className="flex items-start justify-between gap-4 mb-3">
        <h3 className="font-serif font-bold text-xl text-[#0A192F] leading-snug">{s.title}</h3>
        <span className="text-caption text-[#B45309] whitespace-nowrap">{s.price}</span>
      </div>
      <p className="text-slate-600 text-[14.5px] leading-relaxed mb-5">{s.desc}</p>
      <ul className="space-y-2 mb-6 flex-1">
        {s.includes.map((x) => (
          <li key={x} className="flex gap-2 text-[13px] text-slate-700"><CheckCircle2 className="w-4 h-4 text-[#15803D] mt-0.5 shrink-0" />{x}</li>
        ))}
      </ul>
      <Link to="/case-intake" className="btn-navy text-[12px] uppercase tracking-widest text-center py-3" data-testid={`service-cta-${i}`}>Request Engagement</Link>
    </div>
  );
}

export function ServicesGrid({ services }) {
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
        <div>
          <div className="gold-underline mb-4" />
          <h2 className="text-3xl lg:text-4xl font-serif font-bold text-[#0A192F]">What we deliver</h2>
        </div>
        <p className="max-w-md text-slate-600 leading-relaxed">Six focused service lines for attorneys, VA-accredited professionals, and federal-facing engagements.</p>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {services.map((s, i) => <ServiceCard key={s.title} s={s} i={i} />)}
      </div>
    </section>
  );
}

export function FeeSection({ feeTiers, hourly, rush }) {
  return (
    <section className="bg-[#F1F5F9] border-y border-slate-200">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="gold-underline mb-4" />
        <h2 className="text-3xl lg:text-4xl font-serif font-bold text-[#0A192F] mb-3">Suggested record review fee schedule</h2>
        <p className="text-slate-600 max-w-3xl mb-10 leading-relaxed">Pricing is designed around the scope and volume of the case — not a per-page charge. Fees below are estimated flat-rate ranges; final pricing may vary with record complexity, condition, deliverables, and turnaround.</p>
        <div className="card-federal overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]" data-testid="fee-table">
            <thead className="bg-[#0A192F] text-white">
              <tr>
                <th className="text-left px-6 py-4 text-caption text-white/70">Medical Record Volume</th>
                <th className="text-left px-6 py-4 text-caption text-white/70">Standard Review</th>
                <th className="text-left px-6 py-4 text-caption text-white/70">Review + Detailed Chronology</th>
                <th className="text-left px-6 py-4 text-caption text-white/70">Comprehensive RN Analysis</th>
              </tr>
            </thead>
            <tbody>
              {feeTiers.map((t, i) => (
                <tr key={t.pages} className={`border-t border-slate-100 ${i % 2 ? "bg-slate-50" : "bg-white"}`} data-testid={`fee-tier-${i}`}>
                  <td className="px-6 py-3.5 font-mono text-[#0A192F]">{t.pages} pages</td>
                  <td className="px-6 py-3.5 font-serif font-bold text-[#0A192F]">{t.std}</td>
                  <td className="px-6 py-3.5 font-serif font-bold text-[#0A192F]">{t.chrono}</td>
                  <td className="px-6 py-3.5 font-serif font-bold text-[#B91C1C]">{t.comp}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="grid md:grid-cols-2 gap-6 mt-8">
          <div className="card-federal p-6">
            <div className="text-caption text-slate-500 mb-4">ADDITIONAL PROFESSIONAL FEES</div>
            <ul className="divide-y divide-slate-100">
              {hourly.map(([k, v]) => (
                <li key={k} className="flex justify-between py-2.5 text-sm"><span className="text-slate-700">{k}</span><span className="font-mono text-[#0A192F]">{v}</span></li>
              ))}
            </ul>
          </div>
          <div className="card-federal p-6">
            <div className="text-caption text-slate-500 mb-4">PRIORITY & RUSH SERVICES</div>
            <ul className="divide-y divide-slate-100">
              {rush.map(([k, v]) => (
                <li key={k} className="flex justify-between py-2.5 text-sm"><span className="text-slate-700">{k}</span><span className="font-mono text-[#B91C1C]">{v}</span></li>
              ))}
            </ul>
            <p className="text-[12px] text-slate-500 mt-4 leading-relaxed">Rush availability depends on case size, complexity, and existing commitments.</p>
          </div>
        </div>
      </div>
    </section>
  );
}

export function PlanCard({ p }) {
  return (
    <div className={`card-federal p-6 flex flex-col ${p.name === "Premier Litigation" ? "border-[#B45309] border-2" : ""}`} data-testid={p.testid}>
      {p.name === "Premier Litigation" && <span className="badge-gold mb-3 self-start">Most Comprehensive</span>}
      <h3 className="font-serif font-bold text-lg text-[#0A192F] leading-snug">{p.name}</h3>
      <div className="mt-2 mb-1">
        <span className="font-serif text-2xl font-bold text-[#B91C1C]">{p.price}</span>
        {p.price !== "Custom" && <span className="text-slate-500 text-sm"> / month</span>}
      </div>
      <div className="text-caption text-slate-500 mb-4">{p.capacity}</div>
      <ul className="space-y-2 mb-6 flex-1">
        {p.includes.map((x) => (
          <li key={x} className="flex gap-2 text-[12.5px] text-slate-700"><CheckCircle2 className="w-3.5 h-3.5 text-[#15803D] mt-0.5 shrink-0" />{x}</li>
        ))}
      </ul>
      <Link to="/contact" className="border border-[#0A192F] text-[11px] uppercase tracking-widest text-center py-2.5 hover:bg-[#0A192F] hover:text-white transition-colors" data-testid={`${p.testid}-cta`}>Discuss Membership</Link>
    </div>
  );
}

export function PlansSection({ plans }) {
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
        <div>
          <div className="gold-underline mb-4" />
          <h2 className="text-3xl lg:text-4xl font-serif font-bold text-[#0A192F]">Recurring counsel memberships</h2>
        </div>
        <p className="max-w-md text-slate-600 leading-relaxed">For firms and VA-accredited professionals with recurring matters — predictable capacity, priority scheduling, and secure collaboration.</p>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-5">
        {plans.map((p) => <PlanCard key={p.name} p={p} />)}
      </div>
    </section>
  );
}

export function TermsSection() {
  return (
    <section className="bg-[#F1F5F9] border-t border-slate-200">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14">
        <div className="flex items-start gap-4">
          <ShieldCheck className="w-6 h-6 text-[#0A192F] shrink-0 mt-1" strokeWidth={1.6} />
          <div className="text-[13px] text-slate-600 leading-relaxed space-y-3 max-w-4xl">
            <p><span className="text-caption block mb-1">TERMS</span>Published fees are estimates and starting rates rather than guaranteed quotations. Final fees are established after an initial assessment of record volume, complexity, requested deliverables, deadline, record condition, and applicable security requirements. A written scope and fee agreement is provided before substantive work begins.</p>
            <p>Legal Nurse Consulting services are medical consulting services and do not constitute legal representation or legal advice. VA claim representation and legal strategy remain the responsibility of the appropriately VA-accredited attorney, agent, or other authorized representative.</p>
          </div>
        </div>
      </div>
    </section>
  );
}
